import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PageName, Stack } from "../components/StyledComponents";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { ContactsSection } from "../features/common/ContactsSection";
import { SubscriptionsSection } from "../features/common/SubscriptionsSection";
import AddMspProductForm from "../features/msp/AddMspProductForm";
import { useCustomerData } from "../hooks/useCustomerData";
import { useCustomerCompanyData } from "../hooks/useCustomerCompanyData";
import { CustomerCompanyInformation } from "../features/common/CustomerCompanyInformation";
import { useParams } from "react-router-dom";
import { setSelectedMspId } from "../features/msp/slices/selectedMspSlice";
import { setSelectedCustomerId } from "../features/msp/slices/selectedCustomerSlice";
import EditCustomerInformationForm from "../features/customer/EditCustomerInformationForm/EditCustomerInformationForm";
import ContactForm from "../features/common/ContactForm";
import { Subscription, UserContact } from "../features/common/types";
import AddCustomerProductLicenseForm from "../features/customer/AddCustomerProductLicenseForm";
import ReduceCustomerProductLicenseForm from "../features/customer/ReduceCustomerProductLicenseForm";
import { Modal } from "../components/Modal";
import { SETI_PRODUCTS } from "../constants/products";
import AddCustomerProductForm from "../features/customer/AddCustomerProductForm";
import ConfirmDialog from "../components/ConfirmDialog";
import { cancelCustomerSubscription, deleteCustomerContact } from "../features/customer/services/customerService";

const CustomerInformation: React.FC = () => {
  const dispatch = useAppDispatch();

  // URL params
  const params = useParams<{ mspId?: string; customerId?: string }>();
  const paramMspId = params.mspId ? Number(params.mspId) : undefined;
  const paramCustomerId = params.customerId
    ? Number(params.customerId)
    : undefined;

  // Redux state
  const selectedMspId = useAppSelector(
    (s: any) => s.selectedMsp?.selectedMspId
  );
  const selectedCustomerId = useAppSelector(
    (s: any) => s.selectedCustomer?.selectedCustomerId
  );

  // prefer URL params, fallback to Redux
  const mspIdToUse = useMemo(
    () => paramMspId ?? selectedMspId,
    [paramMspId, selectedMspId]
  );
  const customerIdToUse = useMemo(
    () => paramCustomerId ?? selectedCustomerId,
    [paramCustomerId, selectedCustomerId]
  );

  // Keep Redux in sync when URL params exist
  useEffect(() => {
    if (paramMspId && paramMspId !== selectedMspId) {
      dispatch(setSelectedMspId(paramMspId));
    }
    if (paramCustomerId && paramCustomerId !== selectedCustomerId) {
      dispatch(setSelectedCustomerId(paramCustomerId));
    }
  }, [
    paramMspId,
    paramCustomerId,
    selectedMspId,
    selectedCustomerId,
    dispatch,
  ]);

  const { customer, fetchData } = useCustomerData(
    Number(mspIdToUse ?? 0),
    Number(customerIdToUse ?? 0)
  );

  const { companyAddress, contacts, subscriptions } =
    useCustomerCompanyData(customer);

  const existingProductNames = useMemo(() => {
    if (!subscriptions) return new Set<string>();
    return new Set(
      subscriptions
        .filter((s: Subscription) => s?.subscription_status === "active")
        .map((s: Subscription) => s?.planPrice?.plan?.product?.product_name)
        .filter(Boolean)
        .map((n: string) => n.toUpperCase().trim())
    );
  }, [subscriptions]);

  // Check which ones exist
  const hasDesktop = existingProductNames.has("SETI-DESKTOP");
  const hasBrowser = existingProductNames.has("SETI-BROWSER");

  // If both present -> no add-product allowed.
  // If one present -> allow adding only the other one (so allowedProducts holds the missing product).
  const allowedProducts = useMemo(() => {
    if (hasDesktop && hasBrowser) return []; // none allowed
    return SETI_PRODUCTS.filter((p) => !existingProductNames.has(p));
  }, [hasDesktop, hasBrowser, existingProductNames]);

  const canAddProduct = allowedProducts.length > 0;
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription>();

  const [showEditContactForm, setShowEditContactForm] = useState(false);
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState<UserContact>();
  const [deletingContact, setDeletingContact] = useState<UserContact>();
  const [showDeleteContact, setShowDeleteContact] = useState(false);
  const [showAddCustomerProductForm, setShowAddCustomerProductForm] = useState(false);
  const [showAddCustomerProductLicenseForm, setShowAddCustomerProductLicenseForm] =
    useState(false);
  const [showReduceCustomerProductLicenseForm, setShowReduceCustomerProductLicenseForm] =
    useState(false);
  const [showCancelCustomerSubscription, setShowCancelCustomerSubscription] =
    useState(false);
  const [showEditCustomerForm, setShowEditCustomerForm] = useState(false);

  const handleEditCompanyInfo = useCallback(
    () => setShowEditCustomerForm(true),
    []
  );
  const handleEditSuccess = useCallback(() => {
    setShowEditCustomerForm(false);
    fetchData();
  }, [fetchData]);

  const handleEditCancel = useCallback(() => {
    setShowEditCustomerForm(false);
  }, []);
  const handleAddContact = useCallback(() => {
    console.log("add customer contact");
    setShowAddContactForm(true);
  }
    , []);
  const handleEditContact = useCallback(
    (c: UserContact) => {
      console.log("edit contact", c);
      setEditingContact(c);
      setShowEditContactForm(true);
    },
    []
  );
  const handleDeleteContact = useCallback(
    (c: UserContact) => {
      console.log("delete contact", c)
      setDeletingContact(c);
      setShowDeleteContact(true);
    },
    []
  );

  const handleAddProduct = useCallback(
    () => {
      setShowAddCustomerProductForm(true);
    },
    []
  );
  const handleAddProductLicense = useCallback(
    (subscription: Subscription) => {
      setSelectedSubscription(subscription);
      setShowAddCustomerProductLicenseForm(true);
    },
    []
  );
  const handleReduceProductLicense = useCallback(
    (subscription: Subscription) => {
      setSelectedSubscription(subscription);
      setShowReduceCustomerProductLicenseForm(true);
    },
    []
  );
  const handleCancelSubscription = useCallback((subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setShowCancelCustomerSubscription(true);
  }, []);

  if (showAddContactForm) {
    return <ContactForm entity_type="customer" action="add" customerId={customerIdToUse!}
      onCancel={() => setShowAddContactForm(false)}
      onSuccess={() => {
        setShowAddContactForm(false);
        fetchData()
      }} />
  }
  if (showEditContactForm) {
    return <ContactForm entity_type="customer" action="edit" customerId={customerIdToUse!} customerContactId={Number(editingContact?.contact_id)}
      onCancel={() => setShowEditContactForm(false)}
      onSuccess={() => {
        setShowEditContactForm(false);
        fetchData()
      }} />
  }

  if (showEditCustomerForm) {
    return (
      <EditCustomerInformationForm
        customer={customer}
        onSuccess={handleEditSuccess}
        onCancel={handleEditCancel}
      />
    );
  }
  return (
    <div style={{ padding: 20 }}>
      <PageName>View Customer: {customer?.organization_name ?? "-"}</PageName>
      <Stack>
        <CustomerCompanyInformation
          customer={customer}
          companyAddress={companyAddress}
          onEdit={handleEditCompanyInfo}
        />

        <ContactsSection
          contacts={contacts}
          onAddContact={handleAddContact}
          onEditContact={handleEditContact}
          onDeleteContact={handleDeleteContact}
        />

        <SubscriptionsSection
          subscriptions={subscriptions}
          onAddProduct={canAddProduct ? handleAddProduct : undefined}
          onAddProductLicense={handleAddProductLicense}
          onReduceProductLicense={handleReduceProductLicense}
          onCancelSubscription={handleCancelSubscription} />
      </Stack>

      {showAddCustomerProductForm && (
        <Modal onClose={() => setShowAddCustomerProductForm(false)} ariaLabel="Add product">
          <AddCustomerProductForm
            allowedProducts={allowedProducts}
            onProductAdded={() => {
              setShowAddCustomerProductForm(false);
              fetchData();
            }}
            onCancel={() => setShowAddCustomerProductForm(false)}
          />
        </Modal>
      )}

      {showAddCustomerProductLicenseForm && (
        <Modal onClose={() => {
          setShowAddCustomerProductLicenseForm(false);
          setSelectedSubscription(undefined);
        }} ariaLabel="Add product license">
          <AddCustomerProductLicenseForm
            subscription={selectedSubscription!}
            onProductLicenseAdded={() => {
              setShowAddCustomerProductLicenseForm(false);
              setSelectedSubscription(undefined);
              fetchData();
            }}
            onCancel={() => {
              setShowAddCustomerProductLicenseForm(false);
              setSelectedSubscription(undefined);
            }}
          />
        </Modal>
      )}

      {showReduceCustomerProductLicenseForm && (
        <Modal onClose={() => {
          setShowReduceCustomerProductLicenseForm(false);
          setSelectedSubscription(undefined);
        }} ariaLabel="Reduce product license">
          <ReduceCustomerProductLicenseForm
            subscription={selectedSubscription!}
            onProductLicenseReduced={() => {
              setShowReduceCustomerProductLicenseForm(false);
              setSelectedSubscription(undefined);
              fetchData();
            }}
            onCancel={() => {
              setShowReduceCustomerProductLicenseForm(false);
              setSelectedSubscription(undefined);
            }}
          />
        </Modal>
      )}

      {showCancelCustomerSubscription && selectedSubscription && (
        <ConfirmDialog
          open={showCancelCustomerSubscription}
          message="Cancel this subscription?"
          onConfirm={async () => {
            setShowCancelCustomerSubscription(false);
            await cancelCustomerSubscription(selectedMspId, selectedCustomerId, selectedSubscription.id);
            console.log("fetch data after cancel");
            await fetchData();
          }}
          onCancel={() => {
            setShowCancelCustomerSubscription(false);
          }
          }
        />
      )}

      {showDeleteContact && deletingContact && (
        <ConfirmDialog
          open={showDeleteContact}
          message="Delete this contact?"
          onConfirm={async () => {
            setShowDeleteContact(false);
            await deleteCustomerContact(customerIdToUse!, Number(deletingContact.contact_id));
            console.log("fetch data after cancel");
            await fetchData();
          }}
          onCancel={() => {
            setShowDeleteContact(false);
          }
          }
        />
      )}

    </div>
  );
};

export default CustomerInformation;
